// Curated cloze deck for the Grammaire track. Sentences are short, modern,
// targeting documented difficulty areas for French speakers learning bokmål.
// Each item: blank + acceptable answers + distractors + FR translation + hint.

import type { ClozeItem } from './cloze-types.ts';

export const CLOZE_DECK: ClozeItem[] = [
  // --- pronoun "man" (A1) ---
  { id: 'c001', cefr: 'A1.1', topic: 'pronoun-man',
    template: 'I Norge ___ man mye kaffe.',
    answers: ['drikker'], distractors: ['drikke', 'drakk', 'drukket'],
    french: 'En Norvège, on boit beaucoup de café.',
    hint: '« Man » est un sujet impersonnel (3ᵉ pers. sg.). Verbe au présent en -er.' },
  { id: 'c002', cefr: 'A1.2', topic: 'pronoun-man',
    template: '___ må vente på bussen.',
    answers: ['man'], distractors: ['en', 'du', 'jeg'],
    french: 'On doit attendre le bus.',
    hint: '« Man » = on (généralité), invariable.' },
  { id: 'c003', cefr: 'A1.3', topic: 'pronoun-man',
    template: 'I Stavanger ___ man eng­elsk over­alt.',
    answers: ['snakker'], distractors: ['snakk', 'snakke', 'snakkes'],
    french: 'À Stavanger, on parle anglais partout.',
    hint: 'Verbe principal au présent après « man » antéposé (V2).' },

  // --- definite suffix (A1) — articles postposés ---
  { id: 'c010', cefr: 'A1.1', topic: 'definite-suffix',
    template: 'Jeg ser bok___.',
    answers: ['a'], distractors: ['en', 'et', 'ene'],
    french: 'Je vois le livre.', // bok = féminin, défini = boka
    hint: 'Féminin singulier défini → suffixe -a.' },
  { id: 'c011', cefr: 'A1.1', topic: 'definite-suffix',
    template: 'Hus___ er gult.',
    answers: ['et'], distractors: ['en', 'a', 'ene'],
    french: 'La maison est jaune.', // hus = neutre
    hint: 'Neutre singulier défini → suffixe -et.' },
  { id: 'c012', cefr: 'A1.2', topic: 'definite-suffix',
    template: 'Gutt___ leker i hagen.',
    answers: ['en'], distractors: ['a', 'et', 'ene'],
    french: 'Le garçon joue dans le jardin.',
    hint: 'Masculin singulier défini → suffixe -en.' },
  { id: 'c013', cefr: 'A1.3', topic: 'definite-suffix',
    template: 'Barn___ leker ute.',
    answers: ['a', 'ene'], distractors: ['en', 'et'],
    french: 'Les enfants jouent dehors.',
    hint: 'Pluriel défini : -a (neutres courts) ou -ene (général).' },

  // --- negation placement (A1) ---
  { id: 'c020', cefr: 'A1.1', topic: 'negation',
    template: 'Jeg liker ___ kaffe.',
    answers: ['ikke'], distractors: ['nei', 'ingen', 'aldri'],
    french: 'Je n’aime pas le café.',
    hint: '« Ikke » se place après le verbe conjugué (S-V-IKKE-O).' },
  { id: 'c021', cefr: 'A1.2', topic: 'negation',
    template: 'Hun har ___ tid i dag.',
    answers: ['ikke'], distractors: ['ingen', 'aldri', 'nei'],
    french: 'Elle n’a pas le temps aujourd’hui.',
    hint: 'Négation simple : « ikke » immédiatement après le verbe.' },
  { id: 'c022', cefr: 'A2.1', topic: 'negation',
    template: 'I går gikk vi ___ på kino.',
    answers: ['ikke'], distractors: ['ingen', 'nei', 'aldri'],
    french: 'Hier, nous ne sommes pas allés au cinéma.',
    hint: 'Avec inversion (V2 après complément), « ikke » suit le sujet.' },

  // --- word order V2 (A1-A2) ---
  { id: 'c030', cefr: 'A1.2', topic: 'word-order-v2',
    template: 'I morgen ___ jeg på jobb.',
    answers: ['drar', 'går', 'reiser'], distractors: ['jeg', 'ikke', 'å'],
    french: 'Demain, je vais au travail.',
    hint: 'Le verbe est toujours en 2ᵉ position : « I morgen [V] jeg … ».' },
  { id: 'c031', cefr: 'A1.3', topic: 'word-order-v2',
    template: 'Klokken sju ___ vi frokost.',
    answers: ['spiser'], distractors: ['spise', 'spiste', 'å spise'],
    french: 'À sept heures, nous prenons le petit-déjeuner.',
    hint: 'V2 : complément circonstanciel → verbe → sujet.' },
  { id: 'c032', cefr: 'A2.1', topic: 'word-order-v2',
    template: 'Hvorfor ___ du så sent?',
    answers: ['kommer', 'kom'], distractors: ['kom du', 'å komme', 'kommet'],
    french: 'Pourquoi viens-tu si tard ?',
    hint: 'Question en mot-Wh- : Wh- + verbe + sujet.' },

  // --- modal verbs (A1) ---
  { id: 'c040', cefr: 'A1.2', topic: 'modal-verbs',
    template: 'Jeg ___ snakke norsk hver dag.',
    answers: ['må', 'vil', 'skal'], distractors: ['å', 'er', 'har'],
    french: 'Je dois / veux / vais parler norvégien chaque jour.',
    hint: 'Verbe modal + infinitif **sans** « å ».' },
  { id: 'c041', cefr: 'A1.3', topic: 'modal-verbs',
    template: 'Du kan ___ hjemme i dag.',
    answers: ['være', 'bli', 'jobbe'], distractors: ['å være', 'er', 'var'],
    french: 'Tu peux rester à la maison aujourd’hui.',
    hint: '« Kan » + infinitif nu (sans å).' },
  { id: 'c042', cefr: 'A2.1', topic: 'modal-verbs',
    template: 'Vi ___ reise til Oslo i sommer.',
    answers: ['skal', 'vil', 'kommer til å'], distractors: ['er', 'har', 'å'],
    french: 'Nous allons partir à Oslo cet été.',
    hint: 'Futur : « skal » (planifié) ou « kommer til å » (prédiction).' },

  // --- infinitive marker å (A1) ---
  { id: 'c050', cefr: 'A1.2', topic: 'infinitive-å',
    template: 'Jeg liker ___ lese bøker.',
    answers: ['å'], distractors: ['og', 'at', 'i'],
    french: 'J’aime lire des livres.',
    hint: '« Å » + infinitif après les verbes de goût (liker, elsker, hater).' },
  { id: 'c051', cefr: 'A1.3', topic: 'infinitive-å',
    template: 'Det er fint ___ være ute i sola.',
    answers: ['å'], distractors: ['og', 'at', 'for'],
    french: 'C’est agréable d’être dehors au soleil.',
    hint: '« Å » introduit l’infinitif sujet réel.' },
  { id: 'c052', cefr: 'A2.1', topic: 'infinitive-å',
    template: 'Hun begynte ___ gråte.',
    answers: ['å'], distractors: ['og', 'at', 'med'],
    french: 'Elle s’est mise à pleurer.',
    hint: 'Après « begynne », « slutte », « prøve » → å + inf.' },

  // --- reflexive seg (A2) ---
  { id: 'c060', cefr: 'A2.1', topic: 'reflexive-seg',
    template: 'Han vasker ___ hver morgen.',
    answers: ['seg'], distractors: ['ham', 'sin', 'sitt'],
    french: 'Il se lave chaque matin.',
    hint: '« Seg » = pronom réfléchi 3ᵉ personne (sg. et pl.).' },
  { id: 'c061', cefr: 'A2.2', topic: 'reflexive-seg',
    template: 'Barna gleder ___ til jul.',
    answers: ['seg'], distractors: ['dem', 'sine', 'meg'],
    french: 'Les enfants se réjouissent à l’approche de Noël.',
    hint: '« Gleder seg » = se réjouir (verbe réfléchi obligatoire).' },
  { id: 'c062', cefr: 'A2.2', topic: 'reflexive-seg',
    template: 'Jeg setter ___ ved vinduet.',
    answers: ['meg'], distractors: ['seg', 'min', 'mitt'],
    french: 'Je m’assieds près de la fenêtre.',
    hint: 'À la 1ʳᵉ pers. sg. : « meg » (pas « seg »).' },

  // --- past tense (A2) ---
  { id: 'c070', cefr: 'A2.1', topic: 'past-tense',
    template: 'I går ___ jeg en god film.',
    answers: ['så', 'leste'], distractors: ['ser', 'sett', 'å se'],
    french: 'Hier, j’ai vu / lu un bon film / livre.',
    hint: 'Preteritum (passé simple/composé français) — verbe fort.' },
  { id: 'c071', cefr: 'A2.1', topic: 'past-tense',
    template: 'Hun har ___ hjemme hele dagen.',
    answers: ['vært'], distractors: ['er', 'var', 'være'],
    french: 'Elle est restée à la maison toute la journée.',
    hint: 'Perfektum : « har » + participe passé. « være » → « vært ».' },
  { id: 'c072', cefr: 'A2.2', topic: 'past-tense',
    template: 'Da han var ung, ___ han i Stavanger.',
    answers: ['bodde'], distractors: ['bor', 'bodd', 'å bo'],
    french: 'Quand il était jeune, il habitait à Stavanger.',
    hint: 'Verbe régulier en -e : preteritum en -de (bo → bodde).' },
  { id: 'c073', cefr: 'A2.3', topic: 'past-tense',
    template: 'Jeg ___ aldri vært i Tromsø før.',
    answers: ['har', 'hadde'], distractors: ['er', 'var', 'å ha'],
    french: 'Je n’avais / n’ai jamais été à Tromsø auparavant.',
    hint: 'Perfektum (har) ou plusquamperfektum (hadde) + vært.' },

  // --- adjective agreement (A2) ---
  { id: 'c080', cefr: 'A2.1', topic: 'adjective-agreement',
    template: 'Et ___ hus står ved sjøen.',
    answers: ['stort'], distractors: ['stor', 'store', 'størst'],
    french: 'Une grande maison se trouve au bord de la mer.',
    hint: 'Adjectif au neutre indéfini : ajouter -t.' },
  { id: 'c081', cefr: 'A2.1', topic: 'adjective-agreement',
    template: 'To ___ biler er parkert utenfor.',
    answers: ['røde'], distractors: ['rød', 'rødt', 'rødest'],
    french: 'Deux voitures rouges sont garées dehors.',
    hint: 'Pluriel : adjectif en -e (forme « faible »).' },
  { id: 'c082', cefr: 'A2.2', topic: 'adjective-agreement',
    template: 'Den ___ kaffen smaker best.',
    answers: ['varme', 'sterke', 'gode'], distractors: ['varm', 'varmt', 'varmest'],
    french: 'Le café chaud / fort / bon a meilleur goût.',
    hint: 'Forme définie (article « den ») : adjectif en -e.' },

  // --- plural nouns (A1-A2) ---
  { id: 'c090', cefr: 'A1.3', topic: 'plural-nouns',
    template: 'Jeg kjøpte to ___ på torget.',
    answers: ['epler'], distractors: ['eple', 'eplet', 'eplene'],
    french: 'J’ai acheté deux pommes au marché.',
    hint: 'Neutre court (1 syllabe) → pluriel souvent en -er.' },
  { id: 'c091', cefr: 'A1.4', topic: 'plural-nouns',
    template: 'Det er mange ___ i havet.',
    answers: ['fisk', 'fisker'], distractors: ['fisken', 'fiskene', 'å fiske'],
    french: 'Il y a beaucoup de poissons dans la mer.',
    hint: 'Neutre 1 syllabe (fisk) : pluriel indéfini = forme nue.' },
  { id: 'c092', cefr: 'A2.1', topic: 'plural-nouns',
    template: 'Vi har tre ___ hjemme.',
    answers: ['barn'], distractors: ['barna', 'barnet', 'barnene'],
    french: 'Nous avons trois enfants à la maison.',
    hint: '« Barn » : pluriel indéfini identique au singulier.' },

  // --- possessive (A2) ---
  { id: 'c100', cefr: 'A2.1', topic: 'possessive',
    template: 'Boka ___ ligger på bordet.',
    answers: ['mi'], distractors: ['min', 'mitt', 'meg'],
    french: 'Mon livre est sur la table.',
    hint: 'Possessif postposé : accord en genre. Bok = féminin → mi.' },
  { id: 'c101', cefr: 'A2.1', topic: 'possessive',
    template: 'Huset ___ er hvitt.',
    answers: ['mitt'], distractors: ['min', 'mi', 'mine'],
    french: 'Ma maison est blanche.',
    hint: 'Neutre : possessif en -tt (mitt, ditt, sitt).' },
  { id: 'c102', cefr: 'A2.2', topic: 'possessive',
    template: 'Vennene ___ kommer i kveld.',
    answers: ['mine'], distractors: ['min', 'mitt', 'mi'],
    french: 'Mes amis viennent ce soir.',
    hint: 'Pluriel : possessif en -e (mine, dine, sine).' },

  // --- relative som (A2-B1) ---
  { id: 'c110', cefr: 'A2.2', topic: 'relative-som',
    template: 'Mannen ___ bor ved siden av, er lærer.',
    answers: ['som'], distractors: ['hvem', 'hva', 'der'],
    french: 'L’homme qui habite à côté est enseignant.',
    hint: '« Som » couvre qui / que / dont / lequel.' },
  { id: 'c111', cefr: 'B1.1', topic: 'relative-som',
    template: 'Filmen ___ vi så i går, var veldig god.',
    answers: ['som'], distractors: ['hva', 'hvilken', 'at'],
    french: 'Le film que nous avons vu hier était très bon.',
    hint: '« Som » = pronom relatif unique (jamais « hva » comme relatif).' },
  { id: 'c112', cefr: 'B1.2', topic: 'relative-som',
    template: 'Det er en bok ___ jeg har lest mange ganger.',
    answers: ['som'], distractors: ['hvilken', 'hva', 'der'],
    french: 'C’est un livre que j’ai lu plusieurs fois.',
    hint: '« Som » peut être complément d’objet direct, sujet, etc.' },

  // --- mixed B1 patterns ---
  { id: 'c120', cefr: 'B1.1', topic: 'word-order-v2',
    template: 'Hvis det regner, ___ vi hjemme.',
    answers: ['blir', 'er'], distractors: ['vi blir', 'å være', 'bli'],
    french: 'S’il pleut, nous resterons à la maison.',
    hint: 'Subordonnée « hvis … » puis principale → verbe en 2ᵉ position (V2).' },
  { id: 'c121', cefr: 'B1.1', topic: 'negation',
    template: 'Han sa at han ___ kunne komme.',
    answers: ['ikke'], distractors: ['kan ikke', 'aldri', 'ingen'],
    french: 'Il a dit qu’il ne pouvait pas venir.',
    hint: 'En subordonnée (at-clause), « ikke » se place **avant** le verbe conjugué.' },
  { id: 'c122', cefr: 'B1.2', topic: 'modal-verbs',
    template: 'Du burde ___ tidligere i kveld.',
    answers: ['legge deg', 'sove'], distractors: ['å legge', 'la deg', 'lagt'],
    french: 'Tu devrais te coucher plus tôt ce soir.',
    hint: '« Burde » (modal de conseil) + infinitif nu + réfléchi 2ᵉ pers.' },
];
